/**
 * Reducer for state.models.eln.
 */

import reactUpdate from 'immutability-helper';
import stringify from 'json-stable-stringify';
import _ from 'underscore';

import {ServerResolutionResult} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {
  BulkResolveStringsToValuesFetchCompletedAction,
  StringResolutionActionType,
  SetCachedStringResolutions,
} from 'notebook/tables/string-resolution/StringResolutionReduxActions';
import {BenchlingAction} from 'redux-stores/Action';

export interface StringResolutionState {
  resolutionResultsByResolutionQuery: {[resolutionQuery: string]: ServerResolutionResult};
  stringResolutionLoadingState: {[resolutionQuery: string]: boolean};
}

export const STRING_RESOLUTION_INITIAL_STATE = {
  // Object from stringified RESOLUTION_QUERY_SHAPE to STRING_RESOLUTION_RESULT_SHAPE
  resolutionResultsByResolutionQuery: {},
  // Object from stringified RESOLUTION_QUERY_SHAPE to boolean representing whether it is currently
  // loading or has failed - in either case we should not attempt to refetch
  stringResolutionLoadingState: {},
};

/**
 * Generates a new StringResolutionState based off of the list of resolutionQueries passed in from
 * the Redux action.
 * @param action Action containing the new resolutionQueries to be saved into the state for a
 * BULK_RESOLVE_STRINGS_TO_VALUES_FETCH_COMPLETED action or the filtered set of resolutionQueries for a
 * SET_CACHED_STRING_RESOLUTIONS action
 * @returns The new state to be appropriately merged or set within the action handler
 */
function reduceForStringResolutions(
  action: BulkResolveStringsToValuesFetchCompletedAction | SetCachedStringResolutions
): StringResolutionState {
  const stringifiedResolutionQueries = action.resolutionQueries.map((resolutionQuery) =>
    stringify(resolutionQuery)
  );
  const resolutionResultsByResolutionQuery = _.object(
    _.zip(stringifiedResolutionQueries, action.resolutionResults)
  );
  const stringResolutionLoadingState = _.mapObject(
    resolutionResultsByResolutionQuery,
    (resolvedValue) => false
  );
  return {resolutionResultsByResolutionQuery, stringResolutionLoadingState};
}

export default function reduceStringResolution(
  state: StringResolutionState = STRING_RESOLUTION_INITIAL_STATE,
  action: BenchlingAction
): StringResolutionState {
  switch (action.type) {
    case StringResolutionActionType.BULK_RESOLVE_STRINGS_TO_VALUES_FETCH_STARTED:
      return reactUpdate(state, {
        stringResolutionLoadingState: {
          $merge: Object.fromEntries(
            action.resolutionQueries.map((resolutionQuery) => [stringify(resolutionQuery), true])
          ),
        },
      });
    case StringResolutionActionType.SET_CACHED_STRING_RESOLUTIONS: {
      const newState = reduceForStringResolutions(action);
      return reactUpdate(state, {
        resolutionResultsByResolutionQuery: {
          $set: newState.resolutionResultsByResolutionQuery,
        },
        stringResolutionLoadingState: {
          $set: newState.stringResolutionLoadingState,
        },
      });
    }
    case StringResolutionActionType.BULK_RESOLVE_STRINGS_TO_VALUES_FETCH_COMPLETED: {
      const newState = reduceForStringResolutions(action);
      return reactUpdate(state, {
        resolutionResultsByResolutionQuery: {
          $merge: newState.resolutionResultsByResolutionQuery,
        },
        stringResolutionLoadingState: {
          $merge: newState.stringResolutionLoadingState,
        },
      });
    }
    case StringResolutionActionType.BULK_RESOLVE_STRINGS_TO_VALUES_FETCH_FAILED:
      // Loading state is a boolean representing whether to block refetching, so we set it to true on failure
      return reactUpdate(state, {
        stringResolutionLoadingState: {
          $merge: Object.fromEntries(
            action.resolutionQueries.map((resolutionQuery) => [stringify(resolutionQuery), true])
          ),
        },
      });
    case StringResolutionActionType.CLEAR_STRING_RESOLUTION_CACHE:
      return STRING_RESOLUTION_INITIAL_STATE;
    default:
      return state;
  }
}
